import React, { useState, useEffect, useMemo } from 'react'
import { Alert, Spinner } from 'react-bootstrap'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import useStores from '../../stores/useStore'
import { debounce } from 'throttle-debounce'
import OfficeItem from './OfficeItem'
import SearchResultAmount from '../Forms/SearchResultAmount'
import TopLevelTreeItem from './TopLevelTreeItem'
import { emptyGuid } from '../../utils/emptyGuid'
import { observer } from 'mobx-react'
import SearchField from '../Forms/SearchField'
import NoDataAlert from '../Configuration/NoDataAlert'

const StyledSearchWrap = styled.div`
  margin-bottom: 1rem;
`

const StyledShowMore = styled.span`
  color: ${(props) => props.theme.primaryColor};
  float: right;
  margin-top: 15px;
  cursor: pointer;
`

const OfficesTree = ({
  onClick,
  isClickable,
  withSearch,
  renderExtraItemContent = () => null,
  parentsWithSelectedChildren,
  getContextMenuOptions,
  showTopLevel,
  allowParentSelection,
  withSelectAllButton,
  setAccountDataByKey,
  selectedOffices,
}) => {
  const [filter, setFilter] = useState('')
  const [filterIsLoading, setFilterIsLoading] = useState(false)
  const [filteredOffices, setFilteredOffices] = useState([])
  const [officesActive, setOfficesActive] = useState({})
  const [selectedOfficeId, setSelectedOfficeId] = useState('')
  const [limit, setLimit] = useState(true)
  const limitLength = 40
  const { t } = useTranslation()
  const { officeStore } = useStores()
  const [allSelected, setAllSelected] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)

  // Observables from store
  const partialOffices = officeStore.loadedOffices
  const hasData = partialOffices && partialOffices.length > 0
  const error = officeStore.error

  const allowParentSelectionValue =
    allowParentSelection ?? officeStore.allowParentSelection

  // Effect to load offices
  useEffect(() => {
    officeStore.getOffices(emptyGuid)
  }, [officeStore])

  // Effect to expand child offices when toggled
  useEffect(() => {
    if (Object.keys(officesActive).length) {
      for (const officeId of Object.keys(officesActive)) {
        if (officesActive[officeId]) {
          officeStore.getOffices(officeId)
        }
      }
    }
  }, [officeStore, officesActive])

  useEffect(() => {
    return () => {
      officeStore.clearError() // Clear the error when the component unmounts
    }
  }, [officeStore])

  // Debounced filter change for searching offices
  const loadFilteredOfficesDebounced = useMemo(
    () =>
      debounce(500, async (value) => {
        if (!value) {
          officeStore.cancelGetFilteredOffices()
          setLimit(false)
          setHasSearched(false)
          return
        }
        try {
          setHasSearched(false)
          setLimit(true)
          setFilteredOffices([])
          setFilterIsLoading(true)
          const loadedOffices = await officeStore.getFilteredOffices(value)
          setFilteredOffices(loadedOffices)
          setHasSearched(true)
        } finally {
          setFilterIsLoading(false)
        }
      }),
    [officeStore]
  )

  const handleChangeFilter = (e) => {
    const { value } = e.target
    setFilter(value)
    loadFilteredOfficesDebounced(value)
  }

  const handleClearClick = () => {
    setFilter('')
    setFilteredOffices([])
    setLimit(false)
  }

  // Computed: Top-level offices for rendering
  const topLevelOffices = useMemo(() => {
    if (filter) {
      return filteredOffices
    }
    return partialOffices.filter((office) => !office.parentOffice)
  }, [partialOffices, filter, filteredOffices])

  const limitedTopLevelOffices = useMemo(() => {
    if (limit) {
      return topLevelOffices.slice(0, limitLength)
    }
    return topLevelOffices
  }, [limit, topLevelOffices])

  // Handle parent office click
  const handleParentOfficeClick = () => {
    officeStore.setError(t('officeAdmin:isGroupItem'))
    setTimeout(() => {
      officeStore.clearError()
    }, 4000)
  }

  const handleToggleSelectAll = (e) => {
    const checked = e.target.checked
    setAllSelected(checked)

    const visibleIds = filter
      ? filteredOffices.map((o) => o.id)
      : partialOffices.map((o) => o.id)

    if (checked) {
      const merged = Array.from(new Set([...selectedOffices, ...visibleIds]))
      setAccountDataByKey('accessibleOffices', merged)
    } else {
      const remaining = selectedOffices.filter((id) => !visibleIds.includes(id))
      setAccountDataByKey('accessibleOffices', remaining)
    }
  }

  return (
    <>
      {withSearch && hasData && (
        <StyledSearchWrap>
          <SearchField
            type='search'
            placeholder={t('officeAdmin:searchOrgaUnit')}
            onChange={handleChangeFilter}
            value={filter}
            onClearClick={handleClearClick}
          />
          {filter && <SearchResultAmount count={topLevelOffices.length} />}
          {withSelectAllButton && (
            <div className='d-flex justify-content-end mt-3 px-3 align-items-center'>
              <label htmlFor='selectCheckbox' className='me-2 mb-0'>
                {t('officeAdmin:selectAll')}
              </label>
              <input
                type='checkbox'
                id='selectCheckbox'
                style={{ marginRight: '10px' }}
                checked={allSelected}
                onChange={handleToggleSelectAll}
              />
            </div>
          )}
        </StyledSearchWrap>
      )}
      {error && <Alert variant='danger'>{error}</Alert>}
      {showTopLevel ? (
        <TopLevelTreeItem
          handleClick={onClick}
          renderExtraItemContent={renderExtraItemContent}
        >
          {limitedTopLevelOffices.map((office) => (
            <OfficeItem
              key={office.id}
              office={office}
              setOfficesActive={setOfficesActive}
              onClick={onClick}
              partialOffices={partialOffices}
              isClickable={isClickable}
              officesActive={officesActive}
              renderExtraItemContent={renderExtraItemContent}
              parentsWithSelectedChildren={parentsWithSelectedChildren}
              selectedOfficeId={selectedOfficeId}
              setSelectedOfficeId={setSelectedOfficeId}
              getContextMenuOptions={getContextMenuOptions}
              onParentOfficeClick={handleParentOfficeClick}
              allowParentSelection={allowParentSelectionValue}
            />
          ))}
        </TopLevelTreeItem>
      ) : (
        limitedTopLevelOffices.map((office) => (
          <OfficeItem
            key={office.id}
            office={office}
            setOfficesActive={setOfficesActive}
            onClick={onClick}
            partialOffices={partialOffices}
            isClickable={isClickable}
            officesActive={officesActive}
            renderExtraItemContent={renderExtraItemContent}
            parentsWithSelectedChildren={parentsWithSelectedChildren}
            selectedOfficeId={selectedOfficeId}
            setSelectedOfficeId={setSelectedOfficeId}
            getContextMenuOptions={getContextMenuOptions}
            onParentOfficeClick={handleParentOfficeClick}
            allowParentSelection={allowParentSelectionValue}
          />
        ))
      )}
      {topLevelOffices.length > limitLength && limit && (
        <div className='d-flex justify-content-center'>
          <StyledShowMore onClick={() => setLimit(false)}>
            {t('officeAdmin:showAll')}
          </StyledShowMore>
        </div>
      )}
      {topLevelOffices.length === 0 &&
        !officeStore.officesLoading &&
        hasSearched &&
        !filterIsLoading && (
          <div className='ps-4 pe-2'>
            <strong>{t('officeAdmin:searchStructureTreeNoresult')}</strong>
          </div>
        )}

      {!hasData &&
        !officeStore.officesLoading &&
        !filterIsLoading &&
        !filter && (
          <div className='ps-4 pe-2'>
            <NoDataAlert text={t('administration:noDataEntered')} />
          </div>
        )}
      {(officeStore.officesLoading || filterIsLoading) && (
        <div className='d-flex justify-content-center'>
          <Spinner animation='border' variant='primary' />
        </div>
      )}
    </>
  )
}

export default observer(OfficesTree)
